import React, { useState, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Webhook,
  Loader2,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import { useFormWebhooks } from '../../hooks/useFormWebhooks';
import type { CreateWebhookInput, UpdateWebhookInput } from '../../hooks/useFormWebhooks';
import { usePlan } from '../../hooks/usePlan';
import { useUpgradeGate } from '../billing/UpgradeGate';
import { Button } from '../Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { Modal } from '../ui/Modal';
import type { FormField, WebhookEventType } from '../../types/forms';

// =====================================================
// TYPES
// =====================================================

interface FormWebhooksProps {
  formId: string;
  fields: FormField[];
  scoringEnabled?: boolean;
}

interface WebhookFormData {
  name: string;
  url: string;
  method: 'POST' | 'PUT';
  headers: { key: string; value: string }[];
  field_mapping: string[];
  include_scores: boolean;
  is_active: boolean;
  retry_count: number;
  event_filter: WebhookEventType;
}

const DEFAULT_FORM_DATA: WebhookFormData = {
  name: 'Webhook',
  url: '',
  method: 'POST',
  headers: [],
  field_mapping: [],
  include_scores: false,
  is_active: true,
  retry_count: 3,
  event_filter: 'response.complete',
};

const EVENT_FILTER_OPTIONS = [
  { value: 'response.complete', label: 'Só completas' },
  { value: 'response.partial', label: 'Só parciais' },
  { value: 'response.all', label: 'Todas respostas' },
];

// =====================================================
// COMPONENT
// =====================================================

export const FormWebhooks: React.FC<FormWebhooksProps> = ({
  formId,
  fields,
  scoringEnabled = false,
}) => {
  const { can } = usePlan();
  const { ensureFeature } = useUpgradeGate();
  const hasWebhookAccess = can('webhook');
  const { webhooks, loading, createWebhook, updateWebhook, deleteWebhook, toggleWebhook } = useFormWebhooks(formId);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<WebhookFormData>(DEFAULT_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [revealedHeaders, setRevealedHeaders] = useState<Set<number>>(new Set());

  const openCreate = () => {
    setEditingId(null);
    setFormData(DEFAULT_FORM_DATA);
    setRevealedHeaders(new Set());
    setShowEditor(true);
  };

  const openEdit = (webhook: any) => {
    setEditingId(webhook.id);
    const headersArray = Object.entries(webhook.headers || {}).map(
      ([key, value]) => ({ key, value: value as string })
    );
    setFormData({
      name: webhook.name,
      url: webhook.url,
      method: webhook.method || 'POST',
      headers: headersArray,
      field_mapping: webhook.field_mapping || [],
      include_scores: webhook.include_scores || false,
      is_active: webhook.is_active,
      retry_count: webhook.retry_count ?? 3,
      event_filter: webhook.event_filter || 'response.complete',
    });
    setRevealedHeaders(new Set());
    setShowEditor(true);
  };

  const handleSave = useCallback(async () => {
    if (!formData.url.trim()) return;

    setIsSaving(true);

    const headersObj: Record<string, string> = {};
    formData.headers.forEach(h => {
      if (h.key.trim()) headersObj[h.key.trim()] = h.value;
    });

    if (editingId) {
      const updates: UpdateWebhookInput = {
        name: formData.name,
        url: formData.url,
        method: formData.method,
        headers: headersObj,
        field_mapping: formData.field_mapping,
        include_scores: formData.include_scores,
        is_active: formData.is_active,
        retry_count: formData.retry_count,
        event_filter: formData.event_filter,
      };
      await updateWebhook(editingId, updates);
    } else {
      const input: CreateWebhookInput = {
        form_id: formId,
        name: formData.name,
        url: formData.url,
        method: formData.method,
        headers: headersObj,
        field_mapping: formData.field_mapping,
        include_scores: formData.include_scores,
        is_active: formData.is_active,
        retry_count: formData.retry_count,
        event_filter: formData.event_filter,
      };
      await createWebhook(input);
    }

    setIsSaving(false);
    setShowEditor(false);
  }, [formData, editingId, formId, createWebhook, updateWebhook]);

  const handleDelete = useCallback(async (webhookId: string) => {
    setDeletingId(webhookId);
    await deleteWebhook(webhookId);
    setDeletingId(null);
  }, [deleteWebhook]);

  const addHeader = () => {
    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '' }],
    }));
  };

  const removeHeader = (index: number) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index),
    }));
    setRevealedHeaders(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const toggleHeaderReveal = (index: number) => {
    setRevealedHeaders(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleFieldMapping = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      field_mapping: prev.field_mapping.includes(fieldId)
        ? prev.field_mapping.filter(id => id !== fieldId)
        : [...prev.field_mapping, fieldId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
      </div>
    );
  }

  // Story BP-5 OQ5: Free users see informative placeholder + inline CTA (not hidden UI).
  if (!hasWebhookAccess) {
    return (
      <div className="text-center py-12 liquid-glass rounded-xl">
        <Webhook className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
        <p className="text-white font-medium text-sm mb-1">Webhooks disponíveis nos planos Pro e Business</p>
        <p className="text-neutral-500 text-xs max-w-md mx-auto mb-5">
          Envie respostas automaticamente para serviços externos (Zapier, Make, endpoints próprios).
          Plano Pro: até 3 webhooks. Business: ilimitado.
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            ensureFeature(
              'webhook',
              'pro',
              'Webhooks permitem enviar respostas automaticamente para serviços externos (Zapier, Make, endpoints próprios).'
            )
          }
        >
          Fazer upgrade pra Pro
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Webhook list */}
      {webhooks.length === 0 ? (
        <div className="text-center py-12 liquid-glass rounded-xl">
          <Webhook className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">Nenhum webhook configurado.</p>
          <p className="text-neutral-500 text-xs mt-1">
            Webhooks permitem enviar respostas automaticamente para serviços externos.
          </p>
          <Button variant="primary" size="sm" className="mt-4" onClick={openCreate}>
            <Plus className="w-3.5 h-3.5" />
            Adicionar webhook
          </Button>
        </div>
      ) : (
        <>
          {webhooks.map(webhook => (
            <div
              key={webhook.id}
              className="liquid-glass rounded-xl p-4 flex items-center justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-white truncate">{webhook.name}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      webhook.is_active
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-neutral-700/50 text-neutral-500'
                    }`}
                  >
                    {webhook.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  <span className="text-xs text-neutral-600 uppercase">{webhook.method}</span>
                  <span className="text-xs text-neutral-500">
                    {webhook.event_filter === 'response.all' ? 'Todas' :
                     webhook.event_filter === 'response.partial' ? 'Parciais' : 'Completas'}
                  </span>
                </div>
                <p className="text-xs text-neutral-400 truncate">{webhook.url}</p>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => toggleWebhook(webhook.id, !webhook.is_active)}
                  className="p-3.5 sm:p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                  title={webhook.is_active ? 'Desativar' : 'Ativar'}
                >
                  {webhook.is_active ? (
                    <ToggleRight className="w-4 h-4 text-green-400" />
                  ) : (
                    <ToggleLeft className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => openEdit(webhook)}
                  className="p-3.5 sm:p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                  title="Editar"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(webhook.id)}
                  disabled={deletingId === webhook.id}
                  className="p-3.5 sm:p-1.5 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Excluir"
                >
                  {deletingId === webhook.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={openCreate}>
            <Plus className="w-3.5 h-3.5" />
            Adicionar webhook
          </Button>
        </>
      )}

      {/* Editor Modal */}
      <Modal
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        title={editingId ? 'Editar Webhook' : 'Novo Webhook'}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Enviar para N8N"
          />

          <Input
            label="URL de destino"
            value={formData.url}
            onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
            placeholder="https://..."
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Método HTTP"
              value={formData.method}
              onChange={(e) => setFormData(prev => ({ ...prev, method: e.target.value as 'POST' | 'PUT' }))}
              options={[
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
              ]}
            />
            <Input
              label="Tentativas de retry"
              type="number"
              value={String(formData.retry_count)}
              onChange={(e) => setFormData(prev => ({ ...prev, retry_count: Math.min(10, Math.max(0, Number(e.target.value))) }))}
              helpText="0-10"
            />
          </div>

          <Select
            label="Eventos que disparam"
            value={formData.event_filter}
            onChange={(e) => setFormData(prev => ({ ...prev, event_filter: e.target.value as WebhookEventType }))}
            options={EVENT_FILTER_OPTIONS}
          />

          {/* Headers */}
          <div>
            <label className="block text-sm font-medium text-neutral-200 mb-2">
              Headers customizados
            </label>
            <p className="text-xs text-neutral-500 mb-2">
              Enviados em toda chamada. Para um Webhook do n8n com Header Auth, use
              o mesmo nome e valor definidos na credencial do nó.
            </p>
            {/* Os dois campos ficam em wrappers flex-1 iguais. O Input sempre
                renderiza um <div class="w-full"> por fora e repassa className para
                o <input> de dentro — por isso o `flex-1` que ficava no Input da
                chave não chegava ao wrapper: ia para o <input>, o wrapper mantinha
                width:100% e o campo de valor, com flex-basis 0, colapsava. */}
            <div className="space-y-2">
              {formData.headers.map((header, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Input
                      value={header.key}
                      onChange={(e) => {
                        const newHeaders = [...formData.headers];
                        newHeaders[index] = { ...newHeaders[index], key: e.target.value };
                        setFormData(prev => ({ ...prev, headers: newHeaders }));
                      }}
                      placeholder="Nome (ex: Authorization)"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input
                      type={revealedHeaders.has(index) ? 'text' : 'password'}
                      value={header.value}
                      onChange={(e) => {
                        const newHeaders = [...formData.headers];
                        newHeaders[index] = { ...newHeaders[index], value: e.target.value };
                        setFormData(prev => ({ ...prev, headers: newHeaders }));
                      }}
                      placeholder="Valor"
                    />
                  </div>
                  <button
                    onClick={() => toggleHeaderReveal(index)}
                    className="p-2 text-neutral-400 hover:text-white transition-colors"
                    title={revealedHeaders.has(index) ? 'Ocultar' : 'Revelar'}
                  >
                    {revealedHeaders.has(index) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => removeHeader(index)}
                    className="p-2 text-neutral-400 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addHeader}
                className="w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors"
              >
                + Adicionar header
              </button>
            </div>
          </div>

          {/* Field Mapping */}
          <div>
            <label className="block text-sm font-medium text-neutral-200 mb-1">
              Campos a enviar
            </label>
            <p className="text-xs text-neutral-500 mb-2">
              Selecione quais campos incluir no payload. Vazio = todos os campos.
            </p>
            <div className="max-h-40 overflow-y-auto space-y-1 p-2 rounded-xl bg-neutral-900/50">
              {fields.filter(f => !['heading', 'paragraph', 'welcome_screen', 'thank_you_screen', 'statement'].includes(f.type)).map(field => (
                <Checkbox
                  key={field.id}
                  label={field.label}
                  checked={formData.field_mapping.includes(field.id)}
                  onChange={() => toggleFieldMapping(field.id)}
                />
              ))}
            </div>
          </div>

          {/* Include Scores */}
          <Checkbox
            label="Incluir pontuação (scores) no payload"
            checked={formData.include_scores}
            onChange={(e) => setFormData(prev => ({ ...prev, include_scores: e.target.checked }))}
            disabled={!scoringEnabled}
          />
          {!scoringEnabled && (
            <p className="text-xs text-neutral-500 -mt-2 ml-6">
              Ative a pontuação nas configurações do formulário primeiro.
            </p>
          )}

          {/* Active toggle */}
          <Checkbox
            label="Webhook ativo"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
          />

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setShowEditor(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleSave}
              disabled={!formData.url.trim() || isSaving}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingId ? 'Salvar' : 'Criar webhook'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
